/* import __COLOCATED_TEMPLATE__ from './question-filters-and-actions.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import {
  type PlaygroundQuestion,
  CustomerAnswerRating,
  AnswerState,
} from 'embercom/lib/fin-playground/question';
import type InboxSearchSuggestionsService from 'embercom/services/inbox-search-suggestions-service';
import { tracked } from '@glimmer/tracking';
import type PlaygroundApi from 'embercom/services/playground-api';
import { getTestingAsLabel } from './settings/helpers';
import type AiContentSegment from 'embercom/models/ai-content-segment';

interface QuestionFiltersAndActionsArgs {
  setQuestionIndex: (index: number) => void;
  selectedQuestionId?: number;
  updateShowConfirmTestRunModal: (showConfirmTestRunModal: boolean) => void;
  updateShowConfirmDeleteQuestionsModal: (showConfirmDeleteQuestionsModal: boolean) => void;
  updateIsDetailsModuleOpen: (isDetailsModuleOpen: boolean) => void;
  openTestSettingsModal: () => void;
  audienceSegments: Array<AiContentSegment>;
}

type QuestionFiltersAndActionsSignature = {
  Args: QuestionFiltersAndActionsArgs;
  Element: HTMLDivElement;
  Blocks: {
    default: [];
  };
};

export default class QuestionFiltersAndActions extends Component<QuestionFiltersAndActionsSignature> {
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare inboxSearchSuggestionsService: InboxSearchSuggestionsService;
  @service declare playgroundApi: PlaygroundApi;
  @service declare appService: $TSFixMe;
  @tracked isTestAsHovered = false;

  CustomerAnswerRating = CustomerAnswerRating;
  AnswerState = AnswerState;

  get answerStateGroupList() {
    return [
      {
        items: [
          {
            text: this.intl.t('ai-agent.playground.filters.answer-state.group-list.all'),
            value: null,
          },
          {
            text: this.intl.t('ai-agent.playground.filters.answer-state.group-list.answered'),
            value: AnswerState.Answered,
          },
          {
            text: this.intl.t('ai-agent.playground.filters.answer-state.group-list.unanswered'),
            value: AnswerState.Unanswered,
          },
        ],
      },
    ];
  }

  get selectedUserName() {
    return (
      this.impersonatedUser?.displayAs ??
      this.intl.t('operator.custom-bot.editor.preview.preview-user')
    );
  }

  get playground() {
    return this.playgroundApi.playground;
  }

  get impersonatedUser() {
    return this.playgroundApi.impersonatedUser;
  }

  get selectedContentSegmentIds() {
    return this.playgroundApi.selectedContentSegmentIds;
  }

  get usingAudienceTargeting() {
    return this.playgroundApi.usingAudienceTargeting;
  }

  get answerStateInfo() {
    switch (this.playground?.questionFilters.answerTypeFilter) {
      case AnswerState.Answered:
        return {
          name: this.intl.t('ai-agent.playground.filters.answer-state.name'),
          label: this.intl.t('ai-agent.playground.filters.answer-state.answered'),
        };
      case AnswerState.Unanswered:
        return {
          name: this.intl.t('ai-agent.playground.filters.answer-state.name'),
          label: this.intl.t('ai-agent.playground.filters.answer-state.unanswered'),
        };
      default:
        return {
          name: this.intl.t('ai-agent.playground.filters.answer-state.name'),
          label: this.intl.t('ai-agent.playground.filters.answer-state.all'),
        };
    }
  }

  get answerRatingGroupList() {
    return [
      {
        items: [
          {
            text: this.intl.t('ai-agent.playground.filters.answer-rating.group-list.any'),
            value: null,
          },
          {
            text: this.intl.t('ai-agent.playground.filters.answer-rating.group-list.positive'),
            value: CustomerAnswerRating.Positive,
          },
          {
            text: this.intl.t('ai-agent.playground.filters.answer-rating.group-list.acceptable'),
            value: CustomerAnswerRating.Acceptable,
          },
          {
            text: this.intl.t('ai-agent.playground.filters.answer-rating.group-list.negative'),
            value: CustomerAnswerRating.Negative,
          },
        ],
      },
    ];
  }

  get answerRatingDetails() {
    switch (this.playground?.questionFilters.ratingFilter) {
      case CustomerAnswerRating.Positive:
        return this.intl.t('ai-agent.playground.filters.answer-rating.group-list.positive');
      case CustomerAnswerRating.Negative:
        return this.intl.t('ai-agent.playground.filters.answer-rating.group-list.negative');
      case CustomerAnswerRating.Acceptable:
        return this.intl.t('ai-agent.playground.filters.answer-rating.group-list.acceptable');
      default:
        return this.intl.t('ai-agent.playground.filters.answer-rating.group-list.any');
    }
  }

  get areFiltersApplied(): boolean {
    return (
      this.playground?.questionFilters.ratingFilter !== null ||
      this.playground?.questionFilters.answerTypeFilter !== null
    );
  }

  get testAsColor() {
    return this.isTestAsHovered ? 'text-accent' : 'text-default';
  }

  get testingAsLabel() {
    return getTestingAsLabel({
      playgroundApi: this.playgroundApi,
      audienceSegments: this.args.audienceSegments,
      intl: this.intl,
    });
  }

  get testingAsIcon() {
    if (this.appService.app.canUseFinPlaygroundTestAsSelector) {
      return this.usingAudienceTargeting ? 'multiple-people' : 'user-id';
    }
    return this.usingAudienceTargeting ? 'multiple-people' : 'person';
  }

  @action updateSelectedQuestionIndex(currentlySelectedQuestionId?: number) {
    let newIndexOfSelectedQuestion = this.playground?.filteredQuestions.findIndex(
      (question: PlaygroundQuestion) => question.id === currentlySelectedQuestionId,
    );

    if (newIndexOfSelectedQuestion !== undefined && newIndexOfSelectedQuestion !== -1) {
      this.args.setQuestionIndex(newIndexOfSelectedQuestion);
    } else {
      this.args.setQuestionIndex(0);
      this.args.updateIsDetailsModuleOpen(true);
    }
  }

  @action updateQuestionRatingFilter(rating: CustomerAnswerRating) {
    let currentlySelectedQuestionId = this.args.selectedQuestionId;
    this.playground?.updateQuestionRatingFilter(rating);

    this.updateSelectedQuestionIndex(currentlySelectedQuestionId);
  }

  @action updateAnswerTypeFilter(filter: AnswerState) {
    let currentlySelectedQuestionId = this.args.selectedQuestionId;
    this.playground?.updateAnswerTypeFilter(filter);

    this.updateSelectedQuestionIndex(currentlySelectedQuestionId);
  }

  @action clearFilters() {
    this.playground?.updateQuestionRatingFilter(null);
    this.playground?.updateAnswerTypeFilter(null);
  }

  @action instrumentButtonClick(buttonType: string, filter?: CustomerAnswerRating | AnswerState) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: buttonType,
      context: 'playground',
      filter,
    });
  }

  @action openTestSettingsModal() {
    this.args.openTestSettingsModal();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::QuestionFiltersAndActions': typeof QuestionFiltersAndActions;
  }
}
